import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { QrCode } from "@/components/QrCode";

export function AdmissionQrCard() {
  const [show, setShow] = useState(false);
  const [fees, setFees] = useState(null);
  const [stds, setStds] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (show && !fees) api.get("/admin/admission/fee-table").then(({ data }) => { setFees(data.fees); setStds(data.standards); }).catch(() => {}); }, [show, fees]);
  const url = `${window.location.origin}/admission`;
  const setFee = (b, s, v) => setFees((x) => ({ ...x, [b]: { ...x[b], [s]: v } }));
  const save = async () => {
    setBusy(true);
    try { await api.put("/admin/admission/fee-table", { fees }); toast.success("Fee table saved (applies to new admissions)"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 mb-4" data-testid="admission-qr-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm font-bold text-blue-900">New admissions come in here from the admission QR form.</p>
        <button type="button" className="text-xs text-blue-700 hover:underline" onClick={() => setShow((s) => !s)} data-testid="admission-qr-toggle">{show ? "Hide" : "Show"} admission QR &amp; fee table</button>
      </div>
      {show && (
        <div className="mt-3 grid gap-4 md:grid-cols-2">
          <div className="rounded-lg bg-white p-3"><p className="text-xs mb-2">Permanent link: <b data-testid="admission-qr-url">{url}</b></p><QrCode value={url} label="PTG New Admission QR" title="New Admission" caption="Scan to fill the PTG admission form" /></div>
          <div className="rounded-lg bg-white p-3 text-sm" data-testid="admission-fee-table">
            <p className="font-semibold mb-2">Monthly fee (Nursery – Std 7). Std 8+ is lump-sum, set by you.</p>
            {!fees ? <Loader2 className="h-4 w-4 animate-spin" /> : (<>
              <div className="grid grid-cols-3 gap-1 text-xs font-semibold text-slate-500 mb-1"><span>Std</span><span>Normal board</span><span>Meghna batch</span></div>
              {stds.map((s) => (
                <div key={s} className="grid grid-cols-3 gap-1 mb-1 items-center">
                  <span className="text-xs">{s}</span>
                  <Input type="number" className="h-8" value={fees.normal[s]} onChange={(e) => setFee("normal", s, e.target.value)} data-testid={`fee-normal-${s}`} />
                  <Input type="number" className="h-8" value={fees.meghna[s]} onChange={(e) => setFee("meghna", s, e.target.value)} data-testid={`fee-meghna-${s}`} />
                </div>
              ))}
              <Button size="sm" className="mt-2 bg-blue-900 hover:bg-blue-950" disabled={busy} onClick={save} data-testid="admission-fee-save">Save fee table</Button>
            </>)}
          </div>
        </div>
      )}
    </div>
  );
}

export function AdmissionDetails({ s }) {
  const a = s.admission;
  if (!a) return null;
  return (
    <div className="mt-1 text-xs text-slate-600 space-y-0.5" data-testid={`admission-details-${s.id}`}>
      <p><b>New admission (QR)</b> by <b>{a.admission_by_name}</b> · Joining {a.joining_month} · {a.batch === "meghna" ? "Meghna private batch" : "Normal board"} · {a.plan_type === "monthly" ? `Monthly ₹${a.monthly_fee}` : "Lump-sum (set fees after approval)"}</p>
      <p>DOB {s.date_of_birth} · {s.school_name} · Parent {s.parent_name} · {s.parent_contact}{s.second_contact ? ` / ${s.second_contact}` : ""}{s.guardian_email ? ` · ${s.guardian_email}` : ""} · {s.address}{s.photo_pending ? " · Photo uploaded (approve in Photos)" : " · No photo yet"}</p>
    </div>
  );
}

export function AgreedMonthlyCard({ sid, plan, onSaved }) {
  const [amt, setAmt] = useState(String(plan?.amount || ""));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!window.confirm(`Set ₹${amt} as the agreed monthly fee? All unpaid months will change; paid months stay the same.`)) return;
    setBusy(true);
    try { const { data } = await api.put(`/fees/students/${sid}/agreed-monthly`, { amount: parseInt(amt, 10) || 0 }); toast.success(`Updated ${data.months_updated} unpaid month(s)`); onSaved && onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-4 space-y-2" data-testid="agreed-monthly-card">
      <h4 className="font-bold text-slate-900">Agreed monthly fee</h4>
      <p className="text-[12px] text-slate-500">If the parent agreed a different monthly amount (e.g. ₹900 instead of ₹950), enter it here. It applies to every unpaid month; paid months are not changed.</p>
      <div className="flex gap-2"><Input type="number" value={amt} onChange={(e) => setAmt(e.target.value)} className="w-36" data-testid="agreed-monthly-input" /><Button onClick={save} disabled={busy} className="bg-blue-900 hover:bg-blue-950" data-testid="agreed-monthly-save">Save</Button></div>
    </div>
  );
}
